import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Box
} from '@material-ui/core';
import { API_BASE_URL } from '@config';

/**
 * Cluster delete confirmation when the cluster has recordings.
 *
 * Three choices:
 *   1. Move recordings to another cluster, then delete cluster
 *   2. Delete cluster AND all its recordings
 *   3. Cancel
 *
 * The caller (ClusterActionMenu) is responsible for invoking the
 * actual deleteCluster after this dialog resolves with onConfirm.
 * We do the recording moves / cascade in here so the cluster only
 * gets deleted once recordings are settled.
 */
const DeleteClusterWithRecordingsDialog = ({
  cluster,
  recordings,
  onConfirm,
  onCancel
}) => {
  const allClusters = useSelector(({ clusters }) => clusters?.cluster?.list) || [];
  const [choice, setChoice] = useState('move');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, label: '' });

  const candidates = useMemo(() => {
    return allClusters.filter((c) => c && c.id && c.id !== cluster.id);
  }, [allClusters, cluster.id]);

  const totalMb = useMemo(() => {
    let bytes = 0;
    for (const r of recordings) bytes += (r && r.sizeBytes) || 0;
    return Math.round(bytes / (1024 * 1024));
  }, [recordings]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      if (choice === 'move') {
        if (!target) { setBusy(false); return; }
        // Move each recording one PATCH at a time. Sequential so a
        // failure mid-way leaves a clear partial state we can surface
        // rather than a silent "some moved, some didn't".
        setProgress({ done: 0, total: recordings.length, label: 'Moving recordings...' });
        for (let i = 0; i < recordings.length; i++) {
          await axios.patch(
            `${API_BASE_URL}/api/recordings/${encodeURIComponent(recordings[i].id)}`,
            { clusterId: target }
          );
          setProgress({ done: i + 1, total: recordings.length, label: 'Moving recordings...' });
        }
      } else {
        // Cascade-delete every recording on this cluster, then proceed.
        setProgress({ done: 0, total: 1, label: 'Deleting recordings...' });
        await axios.delete(
          `${API_BASE_URL}/api/recordings`,
          { params: { clusterId: cluster.id } }
        );
        setProgress({ done: 1, total: 1, label: 'Deleting recordings...' });
      }
      onConfirm();
    } catch (err) {
      setBusy(false);
      // Bubble up - dialog stays open, user can retry or cancel.
      // eslint-disable-next-line no-alert
      window.alert('Recording handling failed: ' + (err.response?.data?.message || err.message || 'unknown'));
    }
  };

  return (
    <Dialog open onClose={busy ? null : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Delete cluster</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          <strong>{cluster.displayName || cluster.name || cluster.id}</strong> has{' '}
          <strong>{recordings.length}</strong> recording{recordings.length !== 1 ? 's' : ''}
          {totalMb > 0 ? ` (${totalMb} MB)` : ''}. Choose what happens to them before the
          cluster is removed.
        </Typography>

        <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)} style={{ marginTop: 12 }}>
          <FormControlLabel
            value="move"
            disabled={busy}
            control={<Radio color="primary" />}
            label="Move recordings to another cluster, then delete this one"
          />
          {choice === 'move' && (
            <Box ml={4} mb={1}>
              <Select
                fullWidth
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                displayEmpty
                disabled={busy}
              >
                <MenuItem value="" disabled>
                  Pick a cluster
                </MenuItem>
                {candidates.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.displayName || c.name || c.id}
                  </MenuItem>
                ))}
              </Select>
              {candidates.length === 0 && (
                <Typography variant="caption" color="error" style={{ marginTop: 8, display: 'block' }}>
                  No other clusters available. Pick "Delete cluster and recordings" instead, or
                  cancel and create another cluster first.
                </Typography>
              )}
            </Box>
          )}
          <FormControlLabel
            value="delete"
            disabled={busy}
            control={<Radio color="primary" />}
            label={`Delete cluster and all ${recordings.length} recording${recordings.length !== 1 ? 's' : ''}`}
          />
        </RadioGroup>

        {busy && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              {progress.label} {progress.total > 1 && `(${progress.done} / ${progress.total})`}
            </Typography>
            <LinearProgress
              variant={progress.total > 1 ? 'determinate' : 'indeterminate'}
              value={progress.total > 1 ? (progress.done / progress.total) * 100 : 0}
              style={{ marginTop: 6 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          color="secondary"
          variant="contained"
          disabled={busy || (choice === 'move' && !target)}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> :
            choice === 'move' ? 'Move and delete cluster' : 'Delete everything'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteClusterWithRecordingsDialog;
